/**
 * What a counter is configured to do, decided by the platform rather than by
 * the build it happens to be running (docs/plans/handheld-fleet.html 11).
 *
 * Two build-time constants become platform facts here, and the mechanism is
 * general enough for the next one. The heartbeat answer carries `config` with a
 * `version`; the device stores it, applies it, and reports `configVersion` back
 * in its telemetry, so a counter that has NOT caught up is visible on the fleet
 * list rather than assumed.
 *
 * ## One object, resolved on the server
 *
 * Three layers - a platform default, a per-location override, a per-device
 * override - are merged in that order BY THE PLATFORM, so the device receives
 * one flat object and never merges anything. A device that merges is a device
 * with a second copy of the precedence rules, on hardware that cannot be
 * updated in an afternoon.
 *
 * ## What is deliberately NOT here
 *
 * Anything about the morning. Acceptance behaviour, service hours and closures
 * are the BAKERY's settings and already have their screens; a device config is
 * about the device. And nothing here carries a path, a URL, a package name or a
 * script, for the same reason the command enum carries none (plan 18).
 */

import { z } from 'zod';
import { patchSchema } from './patch';

export const HandheldConfig = z.object({
  /**
   * Monotonic, and the whole point of the field: the device echoes it in its
   * telemetry, so "has this counter taken the new setting" is a comparison an
   * operator can read rather than an assumption.
   */
  version: z.int().nonnegative(),
  /** Today `BuildConfig.ALARM_FORCES_VOLUME`, true in release builds; a platform switch from now on. */
  alarmForcesVolume: z.boolean(),
  /** How loud, 0-100, of the alarm stream; the counter cannot turn it below this while armed. */
  alarmMinimumVolumePercent: z.int().min(0).max(100),
  /** Seconds between beats while the board is in front. 30 today; 15 during a rollout is the use. */
  heartbeatSeconds: z.int().min(10).max(120),
  /** Whether the staged download of section 4 may happen on cellular, or waits for Wi-Fi. */
  stageOnCellular: z.boolean(),
  /**
   * The two administered thresholds a BAKER's banner is judged against
   * (OPS-031, BAK-060), carried here because the device has to judge them
   * itself: the counter's screen says "battery low" or "storage nearly full"
   * between beats, and a number compiled into the APK would be a second
   * definition of "low" that no operator can move and no deploy reaches for a
   * fortnight.
   *
   * They are NOT settings a layer overrides - `HandheldConfigSettings` omits
   * them for that reason. They are the platform's own `HandheldThresholds`
   * (`internal/index.ts`), the same numbers the fleet alerts are raised on, put
   * on the beat so the device and the desk agree about what is wrong. One
   * source, two readers.
   */
  batteryLowPercent: z.int().min(1).max(99),
  storageLowMb: z.int().min(1).max(1_000_000),
});
export type HandheldConfig = z.infer<typeof HandheldConfig>;

/**
 * The settings without the version - the part a layer can actually hold.
 *
 * `version` is DERIVED from when the layers were written and is never stored by
 * one of them: a number an operator could type would be a number two layers
 * could disagree about, and the device would believe whichever answered last.
 *
 * The two thresholds are omitted for the neighbouring reason: they are
 * administered once for the whole fleet, on the thresholds screen, and a
 * per-shop override of "low battery" would be a second place the same alert is
 * decided from - the desk raising DEVICE_BATTERY_LOW at 15 while the counter's
 * own banner waited for 5.
 */
export const HandheldConfigSettings = HandheldConfig.omit({
  version: true,
  batteryLowPercent: true,
  storageLowMb: true,
});
export type HandheldConfigSettings = z.infer<typeof HandheldConfigSettings>;

/**
 * What ONE layer stores: only the settings that layer overrides.
 *
 * An absent key inherits from the layer above; there is no "same as the
 * default" value, because a layer that stored the default would silently pin it
 * against a later change to the platform's own.
 */
export const HandheldConfigOverride = HandheldConfigSettings.partial();
export type HandheldConfigOverride = z.infer<typeof HandheldConfigOverride>;

/**
 * The PUT body for any of the three layers, on the platform's one patch
 * contract (`./patch`): absent leaves the override as it was, a value sets it,
 * and `null` REMOVES it so the layer above is inherited again. That third state
 * is what "Use default" on the device page sends, and it is the reason this is
 * built from a nullable shape rather than from `HandheldConfigOverride`.
 */
export const HandheldConfigPatch = patchSchema(
  z.object({
    alarmForcesVolume: HandheldConfig.shape.alarmForcesVolume.nullable(),
    alarmMinimumVolumePercent: HandheldConfig.shape.alarmMinimumVolumePercent.nullable(),
    heartbeatSeconds: HandheldConfig.shape.heartbeatSeconds.nullable(),
    stageOnCellular: HandheldConfig.shape.stageOnCellular.nullable(),
  }),
);
export type HandheldConfigPatch = z.infer<typeof HandheldConfigPatch>;

/**
 * What every config endpoint answers: what THIS layer says, and what a device
 * under it actually gets.
 *
 * Both halves, always, because an override screen that showed only the override
 * cannot answer the question an operator opens it with - "what is this counter
 * running now" - and one that showed only the resolved value could not tell an
 * inherited 30 from a pinned 30.
 */
export const HandheldConfigLayer = z.object({
  override: HandheldConfigOverride,
  resolved: HandheldConfig,
});
export type HandheldConfigLayer = z.infer<typeof HandheldConfigLayer>;
